import { getRandomPartialColor } from '$lib/helper/color';
import { generateUUID } from '$lib/helper/uuid';
import { businessStore } from '$lib/stores/business';
import { t } from '$lib/stores/i18n';
import { sessionStore } from '$lib/stores/session';
import { DateTime } from 'luxon';
import { derived, get } from 'svelte/store';
import {
	getMaxCustomerDuration,
	getMinCustomerDuration,
	getSessionMaxCapacity,
	type Session
} from './session';

export type SessionOrder = {
	id: number;
	orderId: string;
	strategy: 'or' | 'parallel';
	sessionIds: Array<Session['id']>;
};

export type GuestPrice = {
	id: string;
	name: string;
	description: string;
	price: number;
	minPax: number;
	maxPax: number;
	validMinPax: number;
	validMaxPax: number;
	archivedAt: DateTime | null;
};

export enum OpeningHourStrategy {
	Follow = 'follow',
	Closed = 'closed',
	Custom = 'custom'
}

export type OfferOverride = {
	validityStart: DateTime | null;
	validityEnd: DateTime | null;
	publishStart: DateTime | null;
	publishEnd: DateTime | null;
	bookingTimeBefore: number | null;
	reconfirmTimeBefore: number | null;
	depositRequiredFrom: number | null;
	depositPercent: number | null;
	depositPricePerPax: number | null;
	depositPrice: number | null;
	allowFullOnlinePayment: boolean;
	allowOfflinePayment: boolean;
	allowDeposit: boolean;
	cancellationTimeBefore: number | null;
	overridePayment: boolean; // Not used for Collection
	overrideBookingTimeBefore: boolean; // Not used for Collection
	overrideCancellation: boolean; // Not used for Collection
	overrideReconfirmTimeBefore: boolean; // Not used for Collection
	overrideValidity: boolean; // Not used for Collection
	overridePublish: boolean; // Not used for Collection
};

export type OfferCollection = {
	id: string;
	name: string;
	description: string;
	offers: Offer['id'][];
	logo: string;
	color: string;
	sortOrder: number;
};

export type Offer = OfferOverride & {
	id: string;
	name: string;
	isActive: boolean;
	sortOrder: number;
	color: string;
	logo: string | null;
	presentation: string;
	shortDescription: string;
	sessionOrders: Array<SessionOrder>;
	isPrivate: boolean;
	minPax: number;
	maxPax: number;
	excludeOtherOffers: boolean;
	maxDuration: number | null;
	minBreakTimeBetweenSession: number;
	basePrice: number;
	guestPrices: Array<GuestPrice>;
	followSessionsOrder: boolean;
	maxBookingPerDay: number;
	collectionId: OfferCollection['id'];
	qrCodeEnabled: boolean;
	archivedAt: DateTime | null;
};

export function getMinPrice(offer: Offer): number {
	const guestPrices = offer.guestPrices.filter((price) => price.archivedAt === null);
	return (
		(guestPrices.length > 0 ? Math.min(...guestPrices.map((price) => price.price)) : 0) +
		offer.basePrice
	);
}
export function getMaxPrice(offer: Offer): number {
	const guestPrices = offer.guestPrices.filter((price) => price.archivedAt === null);
	return (
		(guestPrices.length > 0 ? Math.max(...guestPrices.map((price) => price.price)) : 0) +
		offer.basePrice
	);
}

export function getOfferMinCapacity(offer: Offer): number {
	const guestPrices = offer.guestPrices.filter((price) => price.archivedAt === null);
	return Math.max(
		guestPrices.length > 0 ? Math.min(...guestPrices.map((p) => p.minPax)) : 0,
		offer.minPax
	);
}

export const getOfferMaxCapacity = derived([getSessionMaxCapacity], ([$getSessionMaxCapacity]) => {
	return (offer: Offer, maxPaxOverride: number | undefined = undefined): number => {
		return Math.min(
			maxPaxOverride ?? offer.maxPax,
			...offer.sessionOrders.map((sessionOrder) =>
				Math.max(
					...sessionOrder.sessionIds.map((id) => {
						const session = get(sessionStore).find((s) => s.id === id && !s.archivedAt);
						return session ? $getSessionMaxCapacity(session) : Infinity;
					})
				)
			)
		);
	};
});

export function getOfferMaxDuration(offer: Offer): number {
	return (
		offer.sessionOrders.reduce(
			(acc, sessionOrder) =>
				acc +
				Math.max(
					...sessionOrder.sessionIds.map((id) => {
						const session = get(sessionStore).find((s) => s.id === id && !s.archivedAt);
						return session ? getMaxCustomerDuration(session) : 0;
					})
				),
			0
		) +
		offer.minBreakTimeBetweenSession * (offer.sessionOrders.length - 1)
	);
}

export function getOfferMinDuration(offer: Offer): number {
	return (
		offer.sessionOrders.reduce(
			(acc, sessionOrder) =>
				acc +
				Math.min(
					...sessionOrder.sessionIds.map((id) => {
						const session = get(sessionStore).find((s) => s.id === id && !s.archivedAt);
						return session ? getMinCustomerDuration(session) : 0;
					})
				),
			0
		) +
		offer.minBreakTimeBetweenSession * (offer.sessionOrders.length - 1)
	);
}

export function newPrice(offer: Offer): GuestPrice {
	const maxPax = get(getOfferMaxCapacity)(offer);

	return {
		id: generateUUID(),
		name: get(t)(`offer.guestPrice.defaultName`),
		description: '',
		price: 0,
		minPax: 1,
		maxPax: maxPax,
		validMinPax: 0,
		validMaxPax: maxPax,
		archivedAt: null
	};
}

export const offerIsBookable = derived([getOfferMaxCapacity], ([$getOfferMaxCapacity]) => {
	return (offer: Offer): boolean => {
		return (
			offer.sessionOrders.length > 0 &&
			offer.minPax > 0 &&
			offer.maxPax > 0 &&
			getOfferMinCapacity(offer) <= $getOfferMaxCapacity(offer) &&
			(offer.maxDuration === null ||
				offer.sessionOrders.length <= 1 ||
				offer.maxDuration >= getOfferMinDuration(offer))
		);
	};
});

export function createNewOffer(): Offer {
	return {
		id: generateUUID(),
		name: '',
		isActive: false,
		color: getRandomPartialColor(),
		logo: null,
		presentation: '',
		shortDescription: '',
		sessionOrders: [],
		isPrivate: false,
		minPax: 1,
		maxPax: 1,
		excludeOtherOffers: false,
		bookingTimeBefore: 0,
		reconfirmTimeBefore: null,
		maxDuration: null,
		minBreakTimeBetweenSession: 0,
		validityStart: null,
		validityEnd: null,
		publishStart: null,
		publishEnd: null,
		basePrice: 0,
		depositRequiredFrom: 1,
		depositPercent: null,
		depositPrice: null,
		depositPricePerPax: null,
		allowFullOnlinePayment: false,
		allowOfflinePayment: true,
		allowDeposit: false,
		cancellationTimeBefore: 0,
		guestPrices: [],
		followSessionsOrder: true,
		archivedAt: null,
		sortOrder: 0,
		maxBookingPerDay: 0,
		overridePayment: true,
		overrideBookingTimeBefore: true,
		overrideCancellation: true,
		overrideReconfirmTimeBefore: true,
		overrideValidity: true,
		overridePublish: true,
		qrCodeEnabled: false,
		collectionId: get(businessStore)!.defaultOfferCollectionId
	};
}

export function copyOfferCollection(collection: OfferCollection): OfferCollection {
	return JSON.parse(JSON.stringify(collection));
}
